<template>
  <PageChrome ref="chrome">
    <template #title>Phone Settings</template>

    <div v-if="!projection" class="loading">Loading…</div>

    <div v-else class="wrap">
      <div v-if="provisioningText" class="provisioning" :class="{ failed: provisioningFailed }" role="status">
        <span class="provisioningDot" aria-hidden="true"></span>
        <span>{{ provisioningText }}</span>
      </div>

      <nav ref="nav" class="tabNav" aria-label="Phone settings sections">
        <button
          v-for="s in PHONE_SETTINGS_SECTIONS"
          :key="s.id"
          type="button"
          class="tabBtn"
          :class="{ active: spy.active.value === domId(s.id) }"
          :aria-current="spy.active.value === domId(s.id) ? 'true' : undefined"
          :aria-label="dotLabel(s.tab, dots[s.id])"
          @click="goTo(s.id)"
        >
          {{ s.tab }}
          <NotifyDot v-if="dots[s.id] !== 'none'" :kind="dots[s.id] as 'todo' | 'wait'" />
        </button>
      </nav>

      <div ref="sections" class="sections">
        <SettingsTable id="numbers" :title="titleOf('numbers')" :state="dots.numbers">
          <TeamNumbersSurface />
        </SettingsTable>

        <SettingsTable id="callbacks" :title="titleOf('callbacks')" :state="dots.callbacks">
          <SettingsRow
            name="Default callback number"
            description="When a prospect calls a pool number back, we ring the rep who last dialed them. If we can’t tell who that was, we ring this number so no callback is missed."
          >
            <CallbackNumberSurface variant="team_default" hide-head />
          </SettingsRow>
        </SettingsTable>

        <SettingsTable id="recordings" :title="titleOf('recordings')" :state="dots.recordings">
          <template #aside>
            <AutosaveBadge
              v-if="guardrails"
              :status="guardrails.status"
              :error-message="guardrails.errorMessage"
              @retry="guardrails.retry()"
            />
          </template>
          <GuardrailsSurface ref="guardrails" />
        </SettingsTable>

        <SettingsTable id="reputation" :title="titleOf('reputation')" :state="dots.reputation">
          <SettingsRow
            name="Business verification"
            description="Registers your company with the phone carriers so your calls carry a verified caller identity."
          >
            <div v-if="businessApproved" class="statusLine">
              <StatusChip tone="success" label="✓ Verified" />
              <span>Verified callers get answered more often.</span>
            </div>
            <div v-else-if="bpInReview" class="statusLine">
              <StatusChip tone="amber" label="In review" />
              <span>Submitted — verification finishes on its own in the background.</span>
            </div>
            <BusinessVerificationWizard v-else layout="page" @home="onWizardHome" />
          </SettingsRow>

          <SettingsRow
            name="Spam-label protection"
            description="Registers your numbers with the analytics networks carriers use to label calls, so yours don’t show up as “Spam likely.”"
          >
            <div v-if="!businessApproved" class="statusLine">
              <StatusChip tone="neutral" label="After verification" />
              <span>Unlocks once your business is verified.</span>
            </div>
            <div v-else-if="spamOn" class="statusLine">
              <StatusChip tone="success" label="✓ On" />
              <span>Keeps your calls from showing up as “Spam likely.”</span>
            </div>
            <div v-else-if="viInReview" class="statusLine">
              <StatusChip tone="amber" label="In review" />
              <span>Submitted — carriers typically review within 1–2 days.</span>
            </div>
            <VoiceIntegrityWizard v-else layout="page" @home="onWizardHome" />
          </SettingsRow>
        </SettingsTable>

        <SettingsTable id="crm" :title="titleOf('crm')" :state="dots.crm">
          <CrmExportCard />
          <CrmPhoneFieldsRow />
          <CrmExportSurface />
        </SettingsTable>
      </div>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import NotifyDot from '../components/NotifyDot.vue'
import AutosaveBadge from '../components/integrations/AutosaveBadge.vue'
import SettingsTable from '../components/dialer/settings/SettingsTable.vue'
import SettingsRow from '../components/dialer/settings/SettingsRow.vue'
import StatusChip from '../components/dialer/settings/StatusChip.vue'
import BusinessVerificationWizard from '../components/dialer/settings/BusinessVerificationWizard.vue'
import VoiceIntegrityWizard from '../components/dialer/settings/VoiceIntegrityWizard.vue'
import TeamNumbersSurface from '../components/dialer/settings/TeamNumbersSurface.vue'
import CallbackNumberSurface from '../components/dialer/settings/CallbackNumberSurface.vue'
import GuardrailsSurface from '../components/dialer/settings/GuardrailsSurface.vue'
import CrmExportCard from '../components/dialer/settings/CrmExportCard.vue'
import CrmPhoneFieldsRow from '../components/dialer/settings/CrmPhoneFieldsRow.vue'
import CrmExportSurface from '../components/dialer/settings/CrmExportSurface.vue'
import {
  PHONE_SETTINGS_SECTIONS,
  inReview,
  sectionDomId as domId,
  spamLabelOn,
  type DotKind,
  type SectionId,
} from '../components/dialer/settings/phoneSettingsNav'
import { useTelephony } from '../composables/useTelephony'
import { useScrollSpy } from '../composables/useScrollSpy'
import { useChatScope } from '../composables/useChatScope'
import { useCurrentUser, loadCurrentUser } from '../../services/user'

const route = useRoute()
const router = useRouter()
const telephony = useTelephony()
const { isAdmin } = useCurrentUser()
const { setScope, clearScope } = useChatScope()

const projection = computed(() => telephony.projection.value)

const NO_DOTS: Record<SectionId, DotKind> = { numbers: 'none', callbacks: 'none', recordings: 'none', reputation: 'none', crm: 'none' }
const dots = computed(() => telephony.sectionDots.value ?? NO_DOTS)

function titleOf(id: SectionId): string {
  return PHONE_SETTINGS_SECTIONS.find((s) => s.id === id)!.title
}
function dotLabel(tab: string, kind: DotKind): string {
  if (kind === 'todo') return `${tab}, needs your attention`
  if (kind === 'wait') return `${tab}, in review`
  return tab
}

// Business / voice-integrity review states decide whether to show the wizard or
// a status line — the wizards themselves only render the editable + rejected views.
const bpState = computed(() => projection.value?.business_profile.state ?? 'not_started')
const viState = computed(() => projection.value?.voice_integrity.state ?? 'not_started')
const businessApproved = computed(() => bpState.value === 'approved')
const spamOn = computed(() => !!projection.value && spamLabelOn(projection.value))
const bpInReview = computed(() => inReview(bpState.value))
const viInReview = computed(() => inReview(viState.value))

const provisioningFailed = computed(() => projection.value?.provisioning.status === 'failed')
const provisioningText = computed(() => {
  const status = projection.value?.provisioning.status
  if (!status || status === 'provisioned') return ''
  if (status === 'failed') return 'We hit a snag setting up calling — please contact support.'
  return 'Setting up your calling account.'
})

// Scroll-spy over PageChrome's scroll container (the window never scrolls here).
const chrome = ref<InstanceType<typeof PageChrome> | null>(null)
const nav = ref<HTMLElement | null>(null)
const sections = ref<HTMLElement | null>(null)
const guardrails = ref<InstanceType<typeof GuardrailsSurface> | null>(null)
const scrollRoot = computed(() => chrome.value?.body ?? null)
const spy = useScrollSpy(scrollRoot, PHONE_SETTINGS_SECTIONS.map((s) => domId(s.id)), nav)

function sectionFromHash(hash: string): SectionId | null {
  const id = hash.replace(/^#/, '')
  return PHONE_SETTINGS_SECTIONS.some((s) => s.id === id) ? (id as SectionId) : null
}

function goTo(id: SectionId): void {
  spy.scrollTo(domId(id))
  if (route.hash !== `#${id}`) void router.replace({ hash: `#${id}` })
}

// A deep link can arrive before the sections exist (projection still loading),
// and sections above the target still grow as their own reads land (caller ids,
// wizard drafts) — so keep the target in place until the user scrolls.
const HASH_SETTLE_MS = 3000
let settleObserver: ResizeObserver | null = null
function stopSettling(): void {
  settleObserver?.disconnect()
  settleObserver = null
}
async function scrollToHash(): Promise<void> {
  const id = sectionFromHash(route.hash)
  if (!id || !projection.value) return
  await nextTick()
  const target = domId(id)
  requestAnimationFrame(() => spy.scrollTo(target, { smooth: false }))
  stopSettling()
  if (!sections.value || typeof ResizeObserver === 'undefined') return
  settleObserver = new ResizeObserver(() => {
    if (spy.pinned.value === target) spy.scrollTo(target, { smooth: false })
    else stopSettling()
  })
  settleObserver.observe(sections.value)
  const observer = settleObserver
  setTimeout(() => { if (settleObserver === observer) stopSettling() }, HASH_SETTLE_MS)
}
let hashHandled = false
watch(projection, (p) => {
  if (p && !hashHandled) {
    hashHandled = true
    void scrollToHash()
  }
})
watch(() => route.hash, (hash, prev) => {
  const id = sectionFromHash(hash)
  // Tab clicks already scrolled; only an outside hash change needs it.
  if (id && spy.active.value !== domId(id) && hash !== prev) void scrollToHash()
})

function onWizardHome(): void {
  // A refresh keeps the status lines current after a submit (the wizard already
  // refreshed; this is belt-and-braces).
  void telephony.refresh()
}

onMounted(async () => {
  // The menu entry is admin-gated, but the route isn't — stop direct-URL access.
  await loadCurrentUser()
  if (!isAdmin.value) {
    void router.replace('/home')
    return
  }
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  // Lets the slow trust-hub refresh follow the facets onto this page.
  telephony.setPageOpen(true)
  if (projection.value) {
    hashHandled = true
    void scrollToHash()
  }
  void telephony.ensure()
  void telephony.refresh()
})
onBeforeUnmount(() => {
  stopSettling()
  clearScope()
  telephony.setPageOpen(false)
  telephony.stopAllPolling()
})
</script>

<style scoped>
.wrap {
  max-width: 1080px;
  /* Rows stack against THIS width (not the viewport), so the layout reacts to the
     chat panel / drawer narrowing the content area too. */
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-bottom: var(--space-8);
}
.loading { padding: var(--space-8); color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.provisioning {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 10px var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-secondary);
}
.provisioning.failed {
  background: var(--hue-amber-soft);
  color: var(--hue-amber);
  border-color: var(--hue-amber-edge);
}
.provisioningDot { width: 8px; height: 8px; border-radius: var(--radius-full); background: currentColor; flex-shrink: 0; }

/* Tab bar — the ReportsPage / CandidatesPage tab look, made sticky inside the
   PageChrome scroller. Sticky rests below the scroller's top padding; pulling it up
   by that padding keeps content from scrolling by above the bar. */
.tabNav {
  position: sticky;
  top: calc(-1 * var(--content-pad-top, 0px));
  z-index: 2;
  display: flex;
  gap: var(--space-1);
  overflow-x: auto;
  scrollbar-width: none;
  background: var(--color-bg-app);
  border-bottom: 1px solid var(--color-border-subtle);
}
.tabNav::-webkit-scrollbar { display: none; }
.tabBtn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-3);
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out);
  white-space: nowrap;
}
.tabBtn:hover { color: var(--color-text-primary); }
.tabBtn.active { color: var(--color-text-primary); font-weight: var(--fw-semibold); }
.tabBtn.active::after {
  content: '';
  position: absolute;
  left: var(--space-3);
  right: var(--space-3);
  bottom: -1px;
  height: 2px;
  background: var(--color-accent);
  border-radius: 1px;
}

.sections { display: flex; flex-direction: column; gap: var(--space-5); }

.statusLine {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
</style>
